import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import {
  DownloadOutlined,
  ExclamationCircleFilled,
  // EyeOutlined,
  WalletOutlined,
} from "@ant-design/icons";
import { uuid } from "@ant-design/plots/es/utils";
import { Button, Dropdown, Space, Tag } from "antd";
import { DotVerticalIcon } from "assets/icons";
import moment from "moment/moment";
import { WORKSPACE_ROLES } from "utils/editorConfig";

import AppButton from "components/button";

import ModalForm from "../../../components/forms/components/modalForm";
import AppTable from "../../../components/table";
import { INVOICE_STATUS, USER_TYPE } from "../../../constants/appConstant";
import { AuthInfo } from "../../../stores/slices/auth";
import {
  doGetInvoicesPage,
  doUpdateInvoices,
  isSaving,
} from "../../../stores/slices/invoice";
import { setHeaderTitle, setIsRefresh } from "../../../stores/slices/layout";
import { getUserDetail } from "../../../stores/slices/user";

import InvoiceForm from "./invoiceForm";

const InvoicesPage = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const authInfo = useSelector(AuthInfo);
  const userInfo = useSelector(getUserDetail);
  const { id } = useParams();
  const [columns, setColumns] = useState([]);
  const [invoiceSelected, setInvoiceSelected] = useState(null);
  const [isOpenForm, setIsOpenForm] = useState(false);
  const [isConfirmCharge, setIsConfirmCharge] = useState(false);

  const [searchParam, setSearchParam] = useState();
  const isLoading = useSelector(isSaving);

  useEffect(() => {
    dispatch(setHeaderTitle("invoices"));

    let params = {
      sortFieldInvoice: ["createdTime"],
      sortDirection: "DESC",
      pageNumber: 1,
      pageSize: 10,
    };

    if (id !== "all" && id !== "default") {
      params.workspaceId = id;
    }
    setSearchParam(params);
  }, []);

  const handleSearch = (selectedKeys, confirm) => {
    confirm();
  };

  const handleFilter = (selectedKeys, confirm) => {
    confirm();
  };

  const handleTableChange = (pagination, filters, sorter) => {
    let newSearchParam = {};
    if (authInfo.type === USER_TYPE.SUPER_ADMIN) {
      filters.workspaceName &&
        (newSearchParam = {
          ...newSearchParam,
          searchKeyWorkspaceName: filters.workspaceName[0],
        });
    }

    filters.invoiceNumber &&
      (newSearchParam = {
        ...newSearchParam,
        searchKeyInvoiceNumber: filters.invoiceNumber[0],
      });

    filters.transaction &&
      (newSearchParam = {
        ...newSearchParam,
        searchKeyTransaction: filters.transaction[0],
      });

    filters.issueTime &&
      filters.issueTime[0] &&
      (newSearchParam = {
        ...newSearchParam,
        fromStartTime: moment(filters.issueTime[0].toString()).format(
          "MM/DD/YYYY"
        ),
      });
    filters.issueTime &&
      filters.issueTime[1] &&
      (newSearchParam = {
        ...newSearchParam,
        toStartTime: moment(filters.issueTime[1].toString()).format(
          "MM/DD/YYYY"
        ),
      });

    filters.amount &&
      filters.amount[0] &&
      (newSearchParam = {
        ...newSearchParam,
        fromPrice: filters.amount[0],
      });
    filters.amount &&
      filters.amount[1] &&
      (newSearchParam = {
        ...newSearchParam,
        toPrice: filters.amount[1],
      });

    filters.invoiceStatus &&
      (newSearchParam = {
        ...newSearchParam,
        invoiceStatuses: filters.invoiceStatus,
      });

    if (sorter && sorter.columnKey && sorter.order) {
      newSearchParam = {
        ...newSearchParam,
        sortFieldInvoice: sorter.columnKey,
        sortDirection: sorter.order === "ascend" ? "ASC" : "DESC",
      };
    } else {
      newSearchParam = {
        ...newSearchParam,
        sortFieldInvoice: "createdTime",
        sortDirection: "DESC",
      };
    }

    if (id !== "all" && id !== "default") {
      newSearchParam.workspaceId = id;
    }

    newSearchParam = {
      ...newSearchParam,
      pageNumber: pagination.current,
      pageSize: pagination.pageSize,
    };
    setSearchParam(newSearchParam);
  };
  const handleFormatDateLocale = date => {
    const browserLocale = navigator.language;
    let dateLocale;
    if (date) {
      dateLocale = moment(date)
        .tz(userInfo?.timeZone || moment.tz.guess())
        .locale(browserLocale);
      return `${dateLocale.format("YYYY-MM-DD")} ${dateLocale.format("LT")}`;
    }
  };

  const actionOptionItems = record => {
    return [
      // {
      //   key: "1",
      //   label: <span>{t("invoices.viewPopupTitle")}</span>,
      //   icon: <EyeOutlined />,
      //   onClick: () => {
      //     setInvoiceSelected(record);
      //     setIsOpenForm(true);
      //   },
      // },
      {
        key: "1",
        label: <span>{t("invoices.charge")}</span>,
        icon: <WalletOutlined />,
        onClick: () => {
          setIsConfirmCharge(true);
          setInvoiceSelected(record);
        },
        disabled:
          authInfo?.type === USER_TYPE.SUPER_ADMIN ||
          authInfo?.workspaceRole === WORKSPACE_ROLES.OPERATOR ||
          (authInfo?.workspaceRole === WORKSPACE_ROLES.ADMIN &&
            record.invoiceStatus === "PAID"),
      },
      {
        key: "2",
        label: <span>{t("invoices.downloadInvoice")}</span>,
        icon: <DownloadOutlined />,
        onClick: () => {
          let link = document.createElement("a");
          link.href = record.invoicePath;
          link.download = record.invoiceNumber;
          link.target = "_blank";
          link.dispatchEvent(new MouseEvent("click"));
        },
      },
    ];
  };

  const generateTableColumns = () => {
    let cols = [];
    if (authInfo.type === USER_TYPE.SUPER_ADMIN) {
      cols.push({
        title: t("invoices.workspace"),
        width: 80,
        dataIndex: ["workspace", "name"],
        key: "workspaceName",
        sorter: true,
        isSearchable: true,
        searchPlaceholder:
          "Search by " + t("invoices.workspace").toString().toLowerCase(),
        ellipsis: {
          showTitle: true,
        },
        render: (_, { workspace }) => (
          <>
            {workspace?.name ? workspace.name : "-"}{" "}
            {workspace.status === "DELETE" ? "(DELETED)" : ""}
          </>
        ),
      });
    }
    cols = [
      ...cols,
      {
        title: t("invoices.invoiceNumber"),
        width: 100,
        dataIndex: "invoiceNumber",
        key: "invoiceNumber",
        sorter: true,
        isSearchable: true,
        searchPlaceholder:
          "Search by " + t("invoices.invoiceNumber").toString().toLowerCase(),
        ellipsis: {
          showTitle: true,
        },
      },
      {
        title: t("invoices.amount"),
        width: 100,
        dataIndex: "amount",
        key: "amount",
        sorter: true,
        isShowPricingRange: true,
        render: (_, { amount }) => (
          <>
            {amount !== 0
              ? "AUD" +
                amount.toLocaleString("en-US", {
                  style: "currency",
                  currency: "USD",
                })
              : "AUD$0"}
          </>
        ),
      },
      {
        title: t("invoices.issueDate"),
        width: 80,
        dataIndex: "issueTime",
        key: "issueTime",
        sorter: true,
        isFilterable: true,
        ellipsis: {
          showTitle: true,
        },
        render: (_, record) => <>{handleFormatDateLocale(record.issueTime)}</>,
      },
      {
        title: t("invoices.transaction"),
        width: 120,
        dataIndex: "transaction",
        key: "transaction",
        sorter: true,
        isSearchable: true,
        searchPlaceholder:
          "Search by " + t("invoices.transaction").toString().toLowerCase(),
        ellipsis: {
          showTitle: true,
        },
      },
      {
        title: t("invoices.status"),
        width: 50,
        dataIndex: "invoiceStatus",
        key: "invoiceStatus",
        filters: [
          {
            text: t("status.paid"),
            value: INVOICE_STATUS.PAID,
          },
          {
            text: t("status.pending"),
            value: INVOICE_STATUS.PENDING,
          },
          {
            text: t("status.fail"),
            value: INVOICE_STATUS.FAIL,
          },
        ],
        render: (_, { invoiceStatus }) => (
          <Tag
            bordered={false}
            style={{ borderRadius: "16px" }}
            color={
              invoiceStatus === INVOICE_STATUS.PAID
                ? "#0E9154"
                : invoiceStatus === INVOICE_STATUS.PENDING
                ? "#ffab00"
                : "#A9122B"
            }
          >
            {invoiceStatus.toUpperCase()}
          </Tag>
        ),
      },
      {
        title: t("invoices.actions"),
        width: 40,
        key: "operation",
        render: (_, record) => {
          return (
            <Space size="small">
              <Dropdown
                trigger={["click"]}
                menu={{
                  items: actionOptionItems(record),
                }}
                placement="bottomRight"
              >
                <Button type="text" shape="circle" size="middle">
                  <DotVerticalIcon />
                </Button>
              </Dropdown>
            </Space>
          );
        },
      },
    ];
    setColumns(cols);
  };

  useEffect(() => {
    generateTableColumns();
  }, []);

  const handleChargeInvoice = async data => {
    const { payload } = await dispatch(doUpdateInvoices(data.id));
    if (payload && payload.status === 200) {
      setIsConfirmCharge(false);
    }
    dispatch(setIsRefresh(uuid()));
  };

  const handleTitleConfirm = () => {
    return (
      <div
        style={{
          display: "flex",
          flexDirection: "row",
          alignItems: "center",
        }}
      >
        <ExclamationCircleFilled style={{ color: "#faad14", fontSize: 24 }} />
        <span style={{ marginLeft: 10, fontSize: 16 }}>
          {t("invoices.charge")}
        </span>
      </div>
    );
  };

  return (
    <div style={invoicesPageStyles.mainContainer}>
      {columns && (
        <AppTable
          api={doGetInvoicesPage(searchParam)}
          columns={
            authInfo?.type === USER_TYPE.SUPER_ADMIN
              ? columns
              : columns.filter(item => item.key !== "transaction" && item)
          }
          searchParam={searchParam}
          handleSearch={handleSearch}
          handleFilter={handleFilter}
          handleTableChange={handleTableChange}
          setSearchParam={setSearchParam}
        />
      )}

      <ModalForm
        title={t("invoices.viewPopupTitle")}
        isOpen={isOpenForm}
        onCancel={() => setIsOpenForm(false)}
        footer={false}
        destroyOnClose={true}
        maskClosable={false}
      >
        <InvoiceForm
          onCancel={() => {
            setInvoiceSelected(null);
            setIsOpenForm(false);
          }}
          invoiceSelected={invoiceSelected}
          userType={authInfo.type}
          handleFormatDateLocale={handleFormatDateLocale}
        />
      </ModalForm>
      <ModalForm
        title={handleTitleConfirm()}
        isOpen={isConfirmCharge}
        onCancel={() => setIsConfirmCharge(false)}
        destroyOnClose={true}
        maskClosable={false}
        footer={[
          <AppButton
            key={"button-cancel"}
            label={t("button.cancel")}
            color={"grey"}
            htmlType="button"
            loading={isLoading}
            onClick={() => {
              setIsConfirmCharge(false);
            }}
          />,
          <AppButton
            key={"button-charge"}
            label={t("button.charge")}
            color={"blue"}
            htmlType="button"
            loading={isLoading}
            type="primary"
            onClick={() => {
              handleChargeInvoice(invoiceSelected);
            }}
          />,
        ]}
      >
        {t("invoices.confirmCharge")}
      </ModalForm>
    </div>
  );
};

const invoicesPageStyles = {
  mainContainer: {
    display: "flex",
    flexDirection: "column",
    // gap: "24px",
    marginBottom: "24px",
  },
};

export default InvoicesPage;
